import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Mail,
  Settings,
  FileText,
  Send,
  Save,
  RefreshCw,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  AlertTriangle,
  Users,
  Clock,
  Calendar,
  Bell,
  GitBranch,
  Building2,
  Briefcase,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/axios';

export default function OnboardingConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [testEmailDialog, setTestEmailDialog] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [documentTypes, setDocumentTypes] = useState([]);
  
  // Form states
  const [smtpForm, setSmtpForm] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    from_email: '',
    from_name: 'Waya Empresas',
    use_tls: true
  });
  
  const [settingsForm, setSettingsForm] = useState({
    password_expiration_hours: 72,
    max_file_size_mb: 25,
    tramite_visible_days: 30,
    required_documents: [],
    notification_roles: ['ADMINISTRADOR', 'COMPLIANCE'],
    notification_emails: [],
    auto_approve: false
  });
  
  // Approval settings
  const [approvalConfig, setApprovalConfig] = useState({
    niveles_aprobacion: 1,
    require_different_approvers: false
  });
  const [savingApproval, setSavingApproval] = useState(false);
  
  const [templatesForm, setTemplatesForm] = useState({
    welcome_email_subject: '',
    welcome_email_body: '',
    submission_email_subject: '',
    submission_email_body: '',
    internal_notification_subject: '',
    internal_notification_body: ''
  });
  
  const [newNotificationEmail, setNewNotificationEmail] = useState('');

  useEffect(() => {
    loadConfig();
    loadDocumentTypes();
    loadApprovalConfig();
  }, []);

  const loadApprovalConfig = async () => {
    try {
      const res = await api.get('/onboarding/config/aprobacion');
      setApprovalConfig(res.data);
    } catch (error) {
      console.error('Error loading approval config:', error);
    }
  };

  const handleSaveApprovalConfig = async () => {
    try {
      setSavingApproval(true);
      await api.put(`/onboarding/config/aprobacion?niveles_aprobacion=${approvalConfig.niveles_aprobacion}&require_different_approvers=${approvalConfig.require_different_approvers}`);
      toast.success(`Configuración actualizada: ${approvalConfig.niveles_aprobacion} nivel(es) de aprobación`);
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSavingApproval(false);
    }
  };

  const loadConfig = async () => {
    try {
      setLoading(true);
      const res = await api.get('/onboarding-config/');
      setConfig(res.data);
      
      // Populate forms
      if (res.data.smtp) {
        setSmtpForm(res.data.smtp);
      }
      if (res.data.onboarding) {
        setSettingsForm({
          ...res.data.onboarding,
          max_file_size_mb: res.data.limits?.max_file_size_mb ?? 25,
        });
      }
      if (res.data.templates) {
        setTemplatesForm(res.data.templates);
      }
    } catch (error) {
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentTypes = async () => {
    try {
      const res = await api.get('/empresas/config/tipos-documento');
      setDocumentTypes(res.data || []);
    } catch (error) {
      console.error('Error loading document types:', error);
    }
  };

  const handleSaveSMTP = async () => {
    try {
      setSaving(true);
      await api.put('/onboarding-config/smtp', smtpForm);
      toast.success('Configuración SMTP guardada');
      loadConfig();
    } catch (error) {
      toast.error('Error al guardar SMTP');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSMTP = async () => {
    try {
      setTesting(true);
      const res = await api.post('/onboarding-config/smtp/test', { email: testEmail || null });
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
      setTestEmailDialog(false);
    } catch (error) {
      toast.error('Error al probar SMTP');
    } finally {
      setTesting(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await api.put('/onboarding-config/settings', settingsForm);
      toast.success('Configuración guardada');
      loadConfig();
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTemplates = async () => {
    try {
      setSaving(true);
      await api.put('/onboarding-config/templates', templatesForm);
      toast.success('Plantillas guardadas');
      loadConfig();
    } catch (error) {
      toast.error('Error al guardar plantillas');
    } finally {
      setSaving(false);
    }
  };

  const toggleDocumentRequired = (docId) => {
    setSettingsForm(prev => {
      const docs = prev.required_documents || [];
      if (docs.includes(docId)) {
        return { ...prev, required_documents: docs.filter(d => d !== docId) };
      } else {
        return { ...prev, required_documents: [...docs, docId] };
      }
    });
  };

  const toggleNotificationRole = (role) => {
    setSettingsForm(prev => {
      const roles = prev.notification_roles || [];
      if (roles.includes(role)) {
        return { ...prev, notification_roles: roles.filter(r => r !== role) };
      } else {
        return { ...prev, notification_roles: [...roles, role] };
      }
    });
  };

  const addNotificationEmail = () => {
    if (newNotificationEmail && !settingsForm.notification_emails.includes(newNotificationEmail)) {
      setSettingsForm(prev => ({
        ...prev,
        notification_emails: [...prev.notification_emails, newNotificationEmail]
      }));
      setNewNotificationEmail('');
    }
  };

  const removeNotificationEmail = (email) => {
    setSettingsForm(prev => ({
      ...prev,
      notification_emails: prev.notification_emails.filter(e => e !== email)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="onboarding-config-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Configuración de Onboarding</h1>
          <p className="text-slate-500">Configure el proceso de alta de empresas por cliente</p>
        </div>
        <Badge 
          className={config?.smtp?.configured ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}
        >
          {config?.smtp?.configured ? (
            <><CheckCircle size={14} className="mr-1" /> SMTP Configurado</>
          ) : (
            <><AlertTriangle size={14} className="mr-1" /> SMTP Pendiente</>
          )}
        </Badge>
      </div>

      <Tabs defaultValue="smtp" className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="smtp" className="flex items-center gap-2">
            <Mail size={16} /> Email SMTP
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings size={16} /> Configuración
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex items-center gap-2">
            <FileText size={16} /> Documentos
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <FileText size={16} /> Plantillas Email
          </TabsTrigger>
        </TabsList>

        {/* Tab SMTP */}
        <TabsContent value="smtp">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail size={20} className="text-blue-600" />
                    Configuración SMTP
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Servidor SMTP</Label>
                      <Input
                        placeholder="smtp.hostinger.com"
                        value={smtpForm.host}
                        onChange={(e) => setSmtpForm({ ...smtpForm, host: e.target.value })}
                        data-testid="smtp-host"
                      />
                    </div>
                    <div>
                      <Label>Puerto</Label>
                      <Input
                        type="number"
                        placeholder="587"
                        value={smtpForm.port}
                        onChange={(e) => setSmtpForm({ ...smtpForm, port: parseInt(e.target.value) || 587 })}
                        data-testid="smtp-port"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Usuario</Label>
                      <Input
                        placeholder="usuario@dominio.com"
                        value={smtpForm.username}
                        onChange={(e) => setSmtpForm({ ...smtpForm, username: e.target.value })}
                        data-testid="smtp-username"
                      />
                    </div>
                    <div>
                      <Label>Contraseña</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={smtpForm.password}
                          onChange={(e) => setSmtpForm({ ...smtpForm, password: e.target.value })}
                          data-testid="smtp-password"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-2 top-1/2 -translate-y-1/2"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Email remitente</Label>
                      <Input
                        placeholder="noreply@empresa.com"
                        value={smtpForm.from_email}
                        onChange={(e) => setSmtpForm({ ...smtpForm, from_email: e.target.value })}
                        data-testid="smtp-from-email"
                      />
                    </div>
                    <div>
                      <Label>Nombre remitente</Label>
                      <Input
                        placeholder="Waya Empresas"
                        value={smtpForm.from_name}
                        onChange={(e) => setSmtpForm({ ...smtpForm, from_name: e.target.value })}
                        data-testid="smtp-from-name"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={smtpForm.use_tls}
                      onCheckedChange={(checked) => setSmtpForm({ ...smtpForm, use_tls: checked })}
                    />
                    <Label>Usar TLS/STARTTLS</Label>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button onClick={handleSaveSMTP} disabled={saving} data-testid="save-smtp-btn">
                      <Save size={16} className="mr-2" />
                      {saving ? 'Guardando...' : 'Guardar SMTP'}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => setTestEmailDialog(true)}
                      disabled={!config?.smtp?.configured}
                      data-testid="test-smtp-btn"
                    >
                      <Send size={16} className="mr-2" />
                      Probar Envío
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Panel lateral: Módulos que usan SMTP */}
            <div className="space-y-4">
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-blue-800">
                    <Info size={16} />
                    Módulos que usan este Email
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-3 p-2 bg-white/60 rounded-lg">
                    <Building2 size={18} className="text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-slate-800">Onboarding</p>
                      <p className="text-xs text-slate-500">Emails de bienvenida, confirmación</p>
                    </div>
                    <CheckCircle size={14} className="ml-auto text-green-500" />
                  </div>
                  <div className="flex items-center gap-3 p-2 bg-white/60 rounded-lg">
                    <GitBranch size={18} className="text-indigo-600" />
                    <div>
                      <p className="text-sm font-medium text-slate-800">Aprobaciones</p>
                      <p className="text-xs text-slate-500">Notificaciones de flujos</p>
                    </div>
                    <CheckCircle size={14} className="ml-auto text-green-500" />
                  </div>
                  <div className="flex items-center gap-3 p-2 bg-white/60 rounded-lg">
                    <Bell size={18} className="text-amber-600" />
                    <div>
                      <p className="text-sm font-medium text-slate-800">Escalaciones</p>
                      <p className="text-xs text-slate-500">Alertas automáticas</p>
                    </div>
                    <CheckCircle size={14} className="ml-auto text-green-500" />
                  </div>
                  <div className="flex items-center gap-3 p-2 bg-white/60 rounded-lg">
                    <Briefcase size={18} className="text-purple-600" />
                    <div>
                      <p className="text-sm font-medium text-slate-800">Casos</p>
                      <p className="text-xs text-slate-500">Asignaciones, cambios</p>
                    </div>
                    <CheckCircle size={14} className="ml-auto text-green-500" />
                  </div>
                </CardContent>
              </Card>
              
              {config?.smtp?.configured && (
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-green-800">
                      <CheckCircle size={20} />
                      <div>
                        <p className="font-medium">SMTP Configurado</p>
                        <p className="text-xs text-green-600">Listo para enviar notificaciones</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Tab Settings */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings size={20} className="text-blue-600" />
                Configuración General
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label className="flex items-center gap-2">
                    <Clock size={16} /> Expiración de clave temporal
                  </Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      type="number"
                      className="w-24"
                      data-testid="config-password-expiration-input"
                      disabled={settingsForm.password_expiration_hours === 0}
                      value={settingsForm.password_expiration_hours === 0 ? '' : settingsForm.password_expiration_hours}
                      onChange={(e) => setSettingsForm({ ...settingsForm, password_expiration_hours: parseInt(e.target.value) || 72 })}
                    />
                    <span className="text-slate-500">horas</span>
                  </div>
                  <label className="flex items-center gap-2 mt-2 text-sm text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="config-no-expiration-checkbox"
                      checked={settingsForm.password_expiration_hours === 0}
                      onChange={(e) => setSettingsForm({ ...settingsForm, password_expiration_hours: e.target.checked ? 0 : 72 })}
                    />
                    Sin vencimiento (la clave nunca expira)
                  </label>
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Calendar size={16} /> Visibilidad del trámite finalizado
                  </Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      type="number"
                      className="w-24"
                      value={settingsForm.tramite_visible_days}
                      onChange={(e) => setSettingsForm({ ...settingsForm, tramite_visible_days: parseInt(e.target.value) || 30 })}
                    />
                    <span className="text-slate-500">días</span>
                  </div>
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <FileText size={16} /> Tamaño máximo de archivo
                  </Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      type="number"
                      className="w-24"
                      data-testid="config-max-file-size-input"
                      value={settingsForm.max_file_size_mb}
                      onChange={(e) => setSettingsForm({ ...settingsForm, max_file_size_mb: parseInt(e.target.value) || 25 })}
                    />
                    <span className="text-slate-500">MB por archivo</span>
                  </div>
                </div>
              </div>

              <div>
                <Label className="flex items-center gap-2 mb-3">
                  <Users size={16} /> Roles que reciben notificaciones
                </Label>
                <div className="flex gap-4">
                  {['ADMINISTRADOR', 'SUPERVISOR', 'COMPLIANCE', 'ANALISTA'].map(role => (
                    <label key={role} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={settingsForm.notification_roles?.includes(role)}
                        onCheckedChange={() => toggleNotificationRole(role)}
                      />
                      <span className="text-sm">{role}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className="flex items-center gap-2 mb-3">
                  <Mail size={16} /> Emails adicionales para notificaciones
                </Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    placeholder="email@ejemplo.com"
                    value={newNotificationEmail}
                    onChange={(e) => setNewNotificationEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addNotificationEmail()}
                  />
                  <Button variant="outline" onClick={addNotificationEmail}>Agregar</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {settingsForm.notification_emails?.map(email => (
                    <Badge key={email} variant="secondary" className="flex items-center gap-1">
                      {email}
                      <XCircle 
                        size={14} 
                        className="cursor-pointer hover:text-red-500"
                        onClick={() => removeNotificationEmail(email)}
                      />
                    </Badge>
                  ))}
                </div>
              </div>

              <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-settings-btn">
                <Save size={16} className="mr-2" />
                {saving ? 'Guardando...' : 'Guardar Configuración'}
              </Button>
            </CardContent>
          </Card>

          {/* Card de Flujo de Aprobación */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GitBranch size={20} className="text-purple-600" />
                Flujo de Aprobación
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label className="text-base font-medium mb-3 block">Niveles de Aprobación</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      onClick={() => setApprovalConfig({ ...approvalConfig, niveles_aprobacion: 1 })}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        approvalConfig.niveles_aprobacion === 1
                          ? 'border-green-500 bg-green-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          approvalConfig.niveles_aprobacion === 1 ? 'border-green-500 bg-green-500' : 'border-slate-300'
                        }`}>
                          {approvalConfig.niveles_aprobacion === 1 && <CheckCircle size={14} className="text-white" />}
                        </div>
                        <span className="font-medium">1 Nivel (Simple)</span>
                      </div>
                      <p className="text-sm text-slate-500 ml-9">
                        Un solo revisor aprueba y la empresa se crea inmediatamente.
                      </p>
                      <div className="mt-3 ml-9 text-xs text-slate-400">
                        Enviado → <span className="text-green-600 font-medium">Aprobado</span>
                      </div>
                    </div>
                    
                    <div
                      onClick={() => setApprovalConfig({ ...approvalConfig, niveles_aprobacion: 2 })}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        approvalConfig.niveles_aprobacion === 2
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          approvalConfig.niveles_aprobacion === 2 ? 'border-purple-500 bg-purple-500' : 'border-slate-300'
                        }`}>
                          {approvalConfig.niveles_aprobacion === 2 && <CheckCircle size={14} className="text-white" />}
                        </div>
                        <span className="font-medium">2 Niveles (Doble Control)</span>
                      </div>
                      <p className="text-sm text-slate-500 ml-9">
                        Requiere aprobación de dos revisores diferentes.
                      </p>
                      <div className="mt-3 ml-9 text-xs text-slate-400">
                        Enviado → <span className="text-cyan-600 font-medium">Nivel 1</span> → <span className="text-green-600 font-medium">Aprobado</span>
                      </div>
                    </div>
                  </div>
                </div>

                {approvalConfig.niveles_aprobacion === 2 && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <Switch
                        checked={approvalConfig.require_different_approvers}
                        onCheckedChange={(checked) => setApprovalConfig({ ...approvalConfig, require_different_approvers: checked })}
                      />
                      <div>
                        <span className="font-medium">Requerir diferentes aprobadores</span>
                        <p className="text-sm text-slate-500">El mismo usuario no puede aprobar Nivel 1 y Nivel 2</p>
                      </div>
                    </label>
                  </div>
                )}
              </div>

              <Button 
                onClick={handleSaveApprovalConfig} 
                disabled={savingApproval}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="save-approval-config-btn"
              >
                <Save size={16} className="mr-2" />
                {savingApproval ? 'Guardando...' : 'Guardar Flujo de Aprobación'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Documents */}
        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText size={20} className="text-blue-600" />
                Documentos Requeridos para Onboarding
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-500 mb-4">
                Seleccione los documentos que serán obligatorios para enviar la solicitud a revisión.
              </p>
              <div className="space-y-3">
                {documentTypes.map(doc => (
                  <label 
                    key={doc.id} 
                    className="flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
                  >
                    <Checkbox
                      checked={settingsForm.required_documents?.includes(doc.id)}
                      onCheckedChange={() => toggleDocumentRequired(doc.id)}
                    />
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{doc.nombre}</p>
                      {doc.descripcion && (
                        <p className="text-sm text-slate-500">{doc.descripcion}</p>
                      )}
                    </div>
                    {settingsForm.required_documents?.includes(doc.id) && (
                      <Badge className="bg-green-100 text-green-700">Requerido</Badge>
                    )}
                  </label>
                ))}
                {documentTypes.length === 0 && (
                  <p className="text-slate-400 text-center py-8">
                    No hay tipos de documento configurados
                  </p>
                )}
              </div>
              <Button onClick={handleSaveSettings} disabled={saving} className="mt-4" data-testid="save-documents-btn">
                <Save size={16} className="mr-2" />
                {saving ? 'Guardando...' : 'Guardar Documentos Requeridos'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Templates */}
        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText size={20} className="text-blue-600" />
                Plantillas de Email
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Welcome Email */}
              <div className="border rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-slate-900">Email de Bienvenida</h3>
                <p className="text-sm text-slate-500">Se envía al representante legal cuando se inicia el trámite</p>
                <div>
                  <Label>Asunto</Label>
                  <Input
                    value={templatesForm.welcome_email_subject}
                    onChange={(e) => setTemplatesForm({ ...templatesForm, welcome_email_subject: e.target.value })}
                    placeholder="Bienvenido a Waya Empresas..."
                  />
                </div>
                <div>
                  <Label>Cuerpo del mensaje</Label>
                  <Textarea
                    rows={8}
                    value={templatesForm.welcome_email_body}
                    onChange={(e) => setTemplatesForm({ ...templatesForm, welcome_email_body: e.target.value })}
                    placeholder="Variables disponibles: {representante_nombre}, {empresa_nombre}, {link}, {email}, {password}, {expiration_hours}, {documentos_requeridos}"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Variables: {'{representante_nombre}'}, {'{empresa_nombre}'}, {'{link}'}, {'{email}'}, {'{password}'}, {'{expiration_hours}'}, {'{documentos_requeridos}'}
                  </p>
                </div>
              </div>

              {/* Submission Email */}
              <div className="border rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-slate-900">Email de Confirmación de Envío</h3>
                <p className="text-sm text-slate-500">Se envía cuando el cliente completa y envía su solicitud</p>
                <div>
                  <Label>Asunto</Label>
                  <Input
                    value={templatesForm.submission_email_subject}
                    onChange={(e) => setTemplatesForm({ ...templatesForm, submission_email_subject: e.target.value })}
                    placeholder="Solicitud enviada..."
                  />
                </div>
                <div>
                  <Label>Cuerpo del mensaje</Label>
                  <Textarea
                    rows={6}
                    value={templatesForm.submission_email_body}
                    onChange={(e) => setTemplatesForm({ ...templatesForm, submission_email_body: e.target.value })}
                    placeholder="Variables: {representante_nombre}, {empresa_nombre}, {tramite_id}, {fecha_envio}"
                  />
                </div>
              </div>

              {/* Internal Notification */}
              <div className="border rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-slate-900">Notificación Interna</h3>
                <p className="text-sm text-slate-500">Se envía al equipo cuando hay una nueva solicitud</p>
                <div>
                  <Label>Asunto</Label>
                  <Input
                    value={templatesForm.internal_notification_subject}
                    onChange={(e) => setTemplatesForm({ ...templatesForm, internal_notification_subject: e.target.value })}
                    placeholder="Nueva solicitud de empresa..."
                  />
                </div>
                <div>
                  <Label>Cuerpo del mensaje</Label>
                  <Textarea
                    rows={6}
                    value={templatesForm.internal_notification_body}
                    onChange={(e) => setTemplatesForm({ ...templatesForm, internal_notification_body: e.target.value })}
                    placeholder="Variables: {empresa_nombre}, {cuit}, {representante_nombre}, {representante_email}, {fecha}, {limite_diario}, {limite_mensual}"
                  />
                </div>
              </div>

              <Button onClick={handleSaveTemplates} disabled={saving} data-testid="save-templates-btn">
                <Save size={16} className="mr-2" />
                {saving ? 'Guardando...' : 'Guardar Plantillas'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Test Email */}
      <Dialog open={testEmailDialog} onOpenChange={setTestEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Probar Envío de Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-slate-500">
              Se enviará un email de prueba para verificar la configuración SMTP.
            </p>
            <div>
              <Label>Email de destino (opcional)</Label>
              <Input
                placeholder={smtpForm.from_email || 'Usar email del remitente'}
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
              />
              <p className="text-xs text-slate-400 mt-1">
                Si deja vacío, se enviará al email del remitente configurado
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestEmailDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleTestSMTP} disabled={testing} data-testid="confirm-test-smtp-btn">
              <Send size={16} className="mr-2" />
              {testing ? 'Enviando...' : 'Enviar Prueba'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
